import { useState, useMemo, useEffect } from 'react';
import { Registration, RegistrationStatusEnum } from '../API';
import useDebouncedValue from './useDebouncedValue';
import { parseAdditionalData } from '../utils/parseAdditionalData';
import { ROWS_PER_PAGE } from '../constants/config';
import { registrationMatchesSearch } from '../utils/partyAttendance';

export type EnrichedRegistration = Registration & {
  membership: string;
  notes: string;
  guests: number;
  checkedInCount: number;
};

export function useAttendeeFiltering(attendees: Registration[]) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 300);
  const [testToggled, setTestToggled] = useState(false);
  const [invitedToggled, setInvitedToggled] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const filteredAttendees = useMemo<EnrichedRegistration[]>(
    () =>
      [...attendees]
        .map(attendee => {
          const parsed = parseAdditionalData(attendee.additionalData);
          return {
            ...attendee,
            membership: parsed.membership || '',
            guests: attendee.guests || 0,
            checkedInCount: attendee.checkedInCount ?? 0,
            notes: parsed.notes || '',
          };
        })
        .sort((a, b) => {
          if (sortConfig !== null) {
            const { key, direction } = sortConfig;
            let aValue = a[key];
            let bValue = b[key];
            if (key === 'notes') {
              aValue = (a.notes ?? '').toLowerCase();
              bValue = (b.notes ?? '').toLowerCase();
            } else {
              if (typeof aValue === 'string') aValue = aValue.toLowerCase();
              if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            }
            if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
          }
          return 0;
        })
        .filter(attendee => {
          const matchesQuery = registrationMatchesSearch(attendee, debouncedQuery);
          const showTestRegistrations = testToggled || attendee.live;
          const showInvitedRegistrations = invitedToggled || attendee.registrationStatus !== RegistrationStatusEnum.INVITED;
          const matchesStatus = statusFilter === 'ALL' || attendee.registrationStatus === statusFilter;
          return matchesQuery && showTestRegistrations && showInvitedRegistrations && matchesStatus;
        }),
    [attendees, debouncedQuery, testToggled, invitedToggled, statusFilter, sortConfig]
  );

  const totalPages = Math.max(1, Math.ceil(filteredAttendees.length / ROWS_PER_PAGE));
  useEffect(() => { setCurrentPage(0); }, [debouncedQuery, testToggled, invitedToggled, statusFilter, sortConfig]);
  useEffect(() => { setCurrentPage(page => Math.min(page, totalPages - 1)); }, [totalPages]);
  const visiblePage = Math.min(currentPage, totalPages - 1);

  const paginatedAttendees = useMemo(
    () => filteredAttendees.slice(visiblePage * ROWS_PER_PAGE, (visiblePage + 1) * ROWS_PER_PAGE),
    [filteredAttendees, visiblePage]
  );

  const handleSort = (columnKey: string) => {
    const direction =
      sortConfig?.key === columnKey && sortConfig.direction === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortConfig({ key: columnKey, direction });
  };

  const handleNextPage = () => setCurrentPage(p => Math.min(p + 1, totalPages - 1));
  const handlePreviousPage = () => setCurrentPage(p => Math.max(p - 1, 0));
  const handleTestToggleChange = (checked: boolean) => setTestToggled(checked);
  const handleInvitedToggleChange = (checked: boolean) => setInvitedToggled(checked);

  return {
    searchQuery, setSearchQuery,
    testToggled, handleTestToggleChange,
    invitedToggled, handleInvitedToggleChange,
    statusFilter, setStatusFilter,
    sortConfig, handleSort,
    currentPage: visiblePage, handleNextPage, handlePreviousPage,
    filteredAttendees, paginatedAttendees, totalPages,
  };
}
